import React, { useEffect, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import HomeworkWorkbench from './components/HomeworkWorkbench'
import { EDUGATE_API_URL } from './config/serviceConfig'

const API_BASE_URL = EDUGATE_API_URL

/**
 * 训练任务对话框：负责数据拉取与提交 API，UI 由 HomeworkWorkbench 承接。
 */
const HomeworkDialog = ({
  open,
  onClose,
  username,
  userInfo,
  homeworks,
  setHomeworks,
  setFlowName,
  onLaunchTraining,
  ws,
  sessionId,
  archivesList,
  isFromMenu,
  focusTaskId = null,
  focusMode = null,
  focusCourseId = null,
  accentColor = '#6f9fff',
  trainingInProgress = false,
  onResumeTraining,
}) => {
  const accent = accentColor || '#6f9fff'
  const [userCourses, setUserCourses] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [loadedArchives, setLoadedArchives] = useState(archivesList || [])
  const [archivesLoading, setArchivesLoading] = useState(false)
  const [archivesError, setArchivesError] = useState('')
  const [studentIdCache, setStudentIdCache] = useState(null)
  const studentIdPromiseRef = useRef(null)

  const getCasUserStudentId = async (employeeNumber, userName, userType = 'bupt_cas') => {
    if (!employeeNumber || employeeNumber === 'undefined' || !userName) return null
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/auth/get-student-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Cas-User-ID': encodeURIComponent(employeeNumber),
          'X-User-Name': encodeURIComponent(userName),
          'X-User-Type': userType,
        },
      })
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json()
      if (data.code === 0 && data.data?.student_id) return data.data.student_id
      return null
    } catch {
      return null
    }
  }

  const getUserStudentId = async () => {
    if (studentIdCache) return studentIdCache
    if (studentIdPromiseRef.current) return studentIdPromiseRef.current

    const promise = (async () => {
      try {
        let studentId = null
        if (userInfo?.studentID) studentId = userInfo.studentID
        else if (userInfo?.studentNumber) studentId = parseInt(userInfo.studentNumber, 10)
        else if (userInfo?.employeeNumber) {
          studentId = await getCasUserStudentId(
            userInfo.employeeNumber,
            userInfo.name || userInfo.username,
            userInfo.user_type || 'bupt_cas',
          )
        }
        if (studentId) setStudentIdCache(studentId)
        return studentId
      } finally {
        studentIdPromiseRef.current = null
      }
    })()

    studentIdPromiseRef.current = promise
    return promise
  }

  const fetchUserCourses = async () => {
    const studentId = await getUserStudentId()
    if (!studentId) return
    try {
      const response = await fetch(
        `${EDUGATE_API_URL}/api/courses/user-enrollments?student_id=${studentId}`,
      )
      const data = await response.json()
      if (data.code === 0) setUserCourses(data.data.enrollments || [])
    } catch (error) {
      console.error('获取用户课程列表错误:', error)
    }
  }

  const fetchHomeworks = async () => {
    const studentId = await getUserStudentId()
    if (!studentId) return
    try {
      const response = await fetch(
        `${EDUGATE_API_URL}/api/homework/list?student_id=${studentId}`,
      )
      const data = await response.json()
      if (data.code === 0) {
        const formatted = (data.data || []).map((hw) => ({
          ...hw,
          TrainingAssignmentID: hw.id,
          Title: hw.title,
          Content: hw.content,
          Deadline: hw.deadline,
          AssignmentStatus: hw.status,
          CourseID: hw.courseId,
          CourseName: hw.courseName,
        }))
        setHomeworks(formatted)
      }
    } catch (error) {
      console.error('获取作业列表错误:', error)
    }
  }

  useEffect(() => {
    if (open && username) {
      fetchUserCourses()
      fetchHomeworks()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, username])

  const buildChatLogIdentityHeaders = async () => {
    const studentId = await getUserStudentId()
    const headers = {}
    if (studentId) headers['X-Student-ID'] = String(studentId)
    if (userInfo?.employeeNumber) {
      headers['X-Cas-User-ID'] = encodeURIComponent(userInfo.employeeNumber)
      headers['X-User-Name'] = encodeURIComponent(userInfo.name || username || '')
      headers['X-User-Type'] = userInfo.user_type || 'bupt_cas'
    }
    return headers
  }

  const fetchArchives = async () => {
    setArchivesLoading(true)
    setArchivesError('')
    try {
      const identityHeaders = await buildChatLogIdentityHeaders()
      const response = await fetch(`${EDUGATE_API_URL}/api/archives/summary?filter=all`, {
        headers: identityHeaders,
      })
      const data = await response.json()
      if (!response.ok || data.code !== 0) {
        throw new Error(data.message || '存档加载失败')
      }
      const summaries = Array.isArray(data.data) ? data.data : []
      setLoadedArchives(summaries)
      return summaries
    } catch (error) {
      console.error('获取训练存档错误:', error)
      setArchivesError(error.message || '存档加载失败，请稍后重试')
      return null
    } finally {
      setArchivesLoading(false)
    }
  }

  const handleSubmitArchive = async (homeworkId, archiveId, message) => {
    setSubmitting(true)
    try {
      const studentId = await getUserStudentId()
      if (!studentId) return false
      const response = await fetch(`${API_BASE_URL}/api/homework/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId,
          homeworkId,
          archiveId,
          message: message || '',
        }),
      })
      const data = await response.json()
      if (data.code === 0) {
        await fetchHomeworks()
        return true
      }
      window.alert(data.message || '作业提交失败')
      return false
    } catch (error) {
      console.error('提交作业时出错:', error)
      window.alert('提交作业时出错')
      return false
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartTraining = (homework) => {
    if (!homework?.processName) return
    onClose?.()
    if (trainingInProgress) {
      onResumeTraining?.()
      return
    }
    if (onLaunchTraining) {
      onLaunchTraining(homework.processName, homework.processRemarks)
      return
    }
    setFlowName?.(homework.processName)
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'set_flow_type',
        FlowName: homework.processName,
        session_id: sessionId,
        trainingProcessAssetId: homework.trainingProcessAssetID || homework.trainingProcessAssetId || homework.marketAssetId || '',
        trainingProcessReleaseId: homework.trainingProcessReleaseID || homework.trainingProcessReleaseId || homework.marketReleaseId || '',
      }))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className="workbench-dialog homework-dialog"
      PaperProps={{
        className: 'workbench-dialog-paper',
        style: { '--workbench-accent': accent },
        sx: {
          width: 'min(1280px, 94vw)',
          height: 'min(880px, 92vh)',
          maxWidth: 'none',
          maxHeight: 'none',
          backgroundColor: '#141a22',
          margin: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '14px',
        },
      }}
    >
      <HomeworkWorkbench
        open={open}
        onClose={onClose}
        homeworks={homeworks || []}
        userCourses={userCourses}
        archivesList={loadedArchives}
        archivesLoading={archivesLoading}
        archivesError={archivesError}
        focusTaskId={focusTaskId}
        focusMode={focusMode}
        focusCourseId={focusCourseId}
        isFromMenu={isFromMenu}
        accentColor={accent}
        onStartTraining={handleStartTraining}
        onSubmitArchive={handleSubmitArchive}
        submitting={submitting}
        getStudentId={getUserStudentId}
        buildIdentityHeaders={buildChatLogIdentityHeaders}
        onRefresh={fetchHomeworks}
        fetchArchives={fetchArchives}
      />
    </Dialog>
  )
}

export default HomeworkDialog
